/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable max-len */
import moment from 'moment';
import { createZonedMoment } from '@src/utils/userTimeZone';

interface RolloverConfig {
  isRolloverPreviousDay: boolean;
  dailyRolloverHour: number;
  dailyRolloverMinute: number;
	shiftStartTime: string;
	shiftEndTime: string;
}

interface ShiftAssignment {
  date: string;
  shift_id: string | null;
  is_rollover_previous_day?: boolean;
  daily_rollover_hour?: number;
  daily_rollover_minute?: number;
}

interface ShiftOption {
  shift_id: string;
  is_rollover_previous_day?: boolean;
  daily_rollover_hour?: number;
  daily_rollover_minute?: number;
	overtime_after?: string;
	overtime_before?: string;
}

interface WeeklyShiftTime {
  shift_time_start_hour: number | null;
  shift_time_start_minute: number | null;
  shift_time_end_hour: number | null;
  shift_time_end_minute: number | null;
  unpaid_durations: Array<{
    unpaid_duration_start_hour: number;
    unpaid_duration_start_minute: number;
    unpaid_duration_end_hour: number;
    unpaid_duration_end_minute: number;
  }>;
}

interface PunchInRule {
  is_rollover_previous_day?: boolean;
  daily_rollover_hour?: number;
  daily_rollover_minute?: number;
	allow_flextime: boolean;
	allow_flextime_after: number;
	allow_flextime_before: number;
	weekly_shift_time?: {
    monday?: WeeklyShiftTime;
    tuesday?: WeeklyShiftTime;
    wednesday?: WeeklyShiftTime;
    thursday?: WeeklyShiftTime;
    friday?: WeeklyShiftTime;
    saturday?: WeeklyShiftTime;
    sunday?: WeeklyShiftTime;
  };
}

/**
 * 根據認列日期取得該日的班別時間（完整日期時間格式）
 *
 * 支援跨夜班別：
 * - 判斷依據：shift_time_end_hour < shift_time_start_hour
 * - 跨夜時：結束時間為隔天
 * - 非跨夜：開始和結束時間為當天
 *
 * @param recognizeDate - 認列日期 (YYYY-MM-DD 格式)
 * @param punchInRule - 打卡規則
 * @returns { startTime: 班別開始時間, endTime: 班別結束時間 } (YYYY-MM-DD HH:mm:ss 格式)
 *
 */
const getShiftTimesFromRule = (
	recognizeDate: string,
	punchInRule: PunchInRule | null,
	timezone?: string,
): { startTime: string; endTime: string } => {
	const defaultResult = { startTime: '', endTime: '' };
	const userMoment = createZonedMoment(timezone);

	if (!recognizeDate || !punchInRule?.weekly_shift_time) {
		return defaultResult;
	}

	const weekdays = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
	const weekday = weekdays[userMoment(recognizeDate).day()];

	const shiftTime = punchInRule.weekly_shift_time[weekday];

	// 檢查是否有該日的班表設定
	if (!shiftTime) {
		return defaultResult;
	}

	const {
		shift_time_start_hour,
		shift_time_start_minute,
		shift_time_end_hour,
		shift_time_end_minute,
	} = shiftTime;

	// 如果上班或下班時間為 null，返回空字串
	if (
		shift_time_start_hour === null ||
    shift_time_start_minute === null ||
    shift_time_end_hour === null ||
    shift_time_end_minute === null
	) {
		return defaultResult;
	}

	// 判斷是否為跨夜班別
	const isCrossDayShift = shift_time_end_hour < shift_time_start_hour;

	// 建立班別開始時間
	const startDateTime = userMoment(recognizeDate)
		.hour(shift_time_start_hour)
		.minute(shift_time_start_minute)
		.second(0);

	// 建立班別結束時間
	let endDateTime = userMoment(recognizeDate);

	// 如果是跨夜班別，結束時間為隔天
	if (isCrossDayShift) {
		endDateTime = endDateTime.add(1, 'day');
	}

	endDateTime
		.hour(shift_time_end_hour)
		.minute(shift_time_end_minute)
		.second(0);

	return {
		startTime: startDateTime.format('YYYY-MM-DD HH:mm:ss'),
		endTime: endDateTime.format('YYYY-MM-DD HH:mm:ss'),
	};
};

/**
 * 根據認列日期和班別取得 rollover 配置
 *
 * 優先順序：
 * 1. 排班制：從 allShiftOption 或 shiftSchedulesAssignments 取得班別時間
 * 2. 固定班：從 punchInRule.weekly_shift_time 取得當日班表時間
 *
 * 時間調整：
 * - shiftStartTime：班別開始時間 + 彈性上班時間 (allow_flextime_before)
 * - shiftEndTime：班別結束時間 - 30 分鐘（加班通常從下班前 30 分開始計算）
 *
 * @param recognizeDate - 認列日期 (YYYY-MM-DD 格式)
 * @param options - 配置選項
 * @returns Rollover 配置物件
 */
export const getRolloverConfig = (
	recognizeDate: string,
	options: {
		shiftId?: string;
		isShowShift?: boolean;
		shiftSchedulesAssignments?: ShiftAssignment[];
		allShiftOption?: ShiftOption[];
		punchInRule?: PunchInRule | null;
		timezone?: string;
	},
): RolloverConfig => {
	const {
		shiftId,
		isShowShift = false,
		shiftSchedulesAssignments = [],
		allShiftOption = [],
		punchInRule = null,
		timezone,
	} = options;
	const userMoment = createZonedMoment(timezone);

	// 如果沒有認列日期，返回預設配置（無 rollover）
	if (!recognizeDate) {
		return {
			isRolloverPreviousDay: false,
			dailyRolloverHour: 0,
			dailyRolloverMinute: 0,
			shiftStartTime: '',
			shiftEndTime: '',
		};
	}

	// === 排班制：使用班別配置 ===
	if (isShowShift && shiftId) {
		const shiftFromOptions = allShiftOption.find(shift => shift.shift_id === shiftId);

		if (shiftFromOptions) {
			// 計算彈性上班時間（如果啟用）
			const flexTimeBefore = punchInRule?.allow_flextime ? (punchInRule?.allow_flextime_before ?? 0) : 0;

			return {
				isRolloverPreviousDay: shiftFromOptions.is_rollover_previous_day ?? false,
				dailyRolloverHour: shiftFromOptions.daily_rollover_hour ?? 0,
				dailyRolloverMinute: shiftFromOptions.daily_rollover_minute ?? 0,
				// 班別加班開始時間 + 30 分鐘 + 彈性上班時間
				shiftStartTime: shiftFromOptions.overtime_before ?
					userMoment(shiftFromOptions.overtime_before)
						.add(30, 'm')
						.add(flexTimeBefore, 'm')
						.format('YYYY-MM-DD HH:mm:ss') :
					'',
				// 班別加班結束時間 - 30 分鐘
				shiftEndTime: shiftFromOptions.overtime_after ?
					userMoment(shiftFromOptions.overtime_after)
						.subtract(30, 'm')
						.format('YYYY-MM-DD HH:mm:ss') :
					'',
			};
		}

		const shiftFromAssignments = shiftSchedulesAssignments.find(
			assignment => (
				assignment.date === userMoment(recognizeDate).format('YYYY-MM-DD') &&
				assignment.shift_id === shiftId &&
				assignment.shift_id !== null
			),
		);
		return {
			isRolloverPreviousDay: shiftFromAssignments?.is_rollover_previous_day ?? false,
			dailyRolloverHour: shiftFromAssignments?.daily_rollover_hour ?? 0,
			dailyRolloverMinute: shiftFromAssignments?.daily_rollover_minute ?? 0,
			shiftStartTime: '',
			shiftEndTime: '',
		};
	}

	// === 固定班：使用打卡規則配置 ===
	const { startTime, endTime } = getShiftTimesFromRule(recognizeDate, punchInRule, timezone);

	return {
		isRolloverPreviousDay: punchInRule?.is_rollover_previous_day ?? false,
		dailyRolloverHour: punchInRule?.daily_rollover_hour ?? 0,
		dailyRolloverMinute: punchInRule?.daily_rollover_minute ?? 0,
		shiftStartTime: startTime,
		shiftEndTime: endTime,
	};
};

/**
 * 驗證打卡時間是否在允許的時間範圍內
 *
 * 允許的時間範圍由 rollover 配置決定：
 * - isRolloverPreviousDay = true: (前一天 rollover時間) ~ (當天 rollover時間-1分鐘)
 * - isRolloverPreviousDay = false: (當天 rollover時間) ~ (隔天 rollover時間-1分鐘)
 *
 * 特殊情況：
 * - rollover = 00:00 時，允許當天整天 (00:00-23:59)
 *
 * @param punchAt - 打卡時間 (YYYY-MM-DD HH:mm 格式)
 * @param recognizeDate - 認列日期 (YYYY-MM-DD 格式)
 * @param isRolloverPreviousDay - 是否從前一天開始
 * @param dailyRolloverHour - 每日 rollover 小時 (0-23)
 * @param dailyRolloverMinute - 每日 rollover 分鐘 (0-59)
 * @returns 打卡時間是否有效
 */
export const isValidPunchTime = (
	punchAt: string,
	recognizeDate: string,
	isRolloverPreviousDay: boolean,
	dailyRolloverHour: number,
	dailyRolloverMinute: number,
	timezone?: string,
): boolean => {
	if (!punchAt || !recognizeDate) return false;

	const userMoment = createZonedMoment(timezone);
	const targetDate = userMoment(recognizeDate);
	const punchAtMoment = userMoment(punchAt);
	const punchAtDate = punchAtMoment.format('YYYY-MM-DD');

	// 特殊情況：rollover = 00:00，只允許當天
	if (dailyRolloverHour === 0 && dailyRolloverMinute === 0) {
		return punchAtDate === targetDate.format('YYYY-MM-DD');
	}

	// 計算允許的日期範圍（兩天）
	let allowedDates: string[];
	if (isRolloverPreviousDay) {
		// 從前一天開始：[前一天, 當天]
		allowedDates = [
			targetDate.clone().subtract(1, 'day').format('YYYY-MM-DD'),
			targetDate.format('YYYY-MM-DD'),
		];
	} else {
		// 從當天開始：[當天, 隔天]
		allowedDates = [
			targetDate.format('YYYY-MM-DD'),
			targetDate.clone().add(1, 'day').format('YYYY-MM-DD'),
		];
	}

	// 檢查日期是否在允許範圍內
	if (!allowedDates.includes(punchAtDate)) {
		return false;
	}

	const punchHour = punchAtMoment.hour();
	const punchMinute = punchAtMoment.minute();

	// 計算起始日期和結束日期
	let startDate: string;
	let endDate: string;

	if (isRolloverPreviousDay) {
		startDate = targetDate.clone().subtract(1, 'day').format('YYYY-MM-DD');
		endDate = targetDate.format('YYYY-MM-DD');
	} else {
		startDate = targetDate.format('YYYY-MM-DD');
		endDate = targetDate.clone().add(1, 'day').format('YYYY-MM-DD');
	}

	// 檢查起始日期的時間限制
	if (punchAtDate === startDate) {
		// 起始日期必須 >= rollover 時間
		if (punchHour < dailyRolloverHour) {
			return false;
		}
		if (punchHour === dailyRolloverHour && punchMinute < dailyRolloverMinute) {
			return false;
		}
	} else if (punchAtDate === endDate) {
		// 結束日期必須 < rollover 時間
		if (dailyRolloverMinute === 0) {
			if (punchHour >= dailyRolloverHour) {
				return false;
			}
		} else {
			if (punchHour > dailyRolloverHour) {
				return false;
			}
			if (punchHour === dailyRolloverHour && punchMinute >= dailyRolloverMinute) {
				return false;
			}
		}
	}

	return true;
};

/**
 * 根據時間和 rollover 規則反推認列日期
 */
export const getRecognizeDateFromTime = (
	dateTime: moment.Moment,
	isRolloverPreviousDay: boolean,
	dailyRolloverHour: number,
	dailyRolloverMinute: number,
): moment.Moment => {
	const date = dateTime.clone().startOf('day');
	const hour = dateTime.hour();
	const minute = dateTime.minute();

	// 判斷時間是否在 rollover 時間之前
	const isBeforeRollover =
    hour < dailyRolloverHour ||
    (hour === dailyRolloverHour && minute < dailyRolloverMinute);

	if (isRolloverPreviousDay) {
		// 從前一天開始：如果時間 >= rollover，屬於當天
		return isBeforeRollover ? date : date.add(1, 'day');
	} else {
		// 從當天開始：如果時間 < rollover，屬於前一天
		return isBeforeRollover ? date.subtract(1, 'day') : date;
	}
};

/**
 * 將時間調整到有效範圍內
 *
 * 如果時間超出有效範圍，會自動調整到邊界：
 * - 小於起始時間 → 調整為起始時間
 * - 大於結束時間 → 調整為結束時間
 * - 在範圍內 → 保持不變
 *
 * 特殊處理：
 * - 如果不允許事後加班 (allowOvertimeRetroactiveRequest = false)
 *   起始時間會取 max(rollover起始時間, 當前時間)
 *
 * @param dateTime - 要調整的時間
 * @param targetDate - 認列日期
 * @param isRolloverPreviousDay - 是否從前一天開始
 * @param dailyRolloverHour - 每日 rollover 小時 (0-23)
 * @param dailyRolloverMinute - 每日 rollover 分鐘 (0-59)
 * @param allowOvertimeRetroactiveRequest - 是否允許事後加班申請 (預設: true)
 * @param currentTime - 當前時間，用於事後加班檢查 (預設: 現在)
 * @returns 調整後的有效時間
 */
export const adjustToValidTime = (
	dateTime: moment.Moment,
	targetDate: moment.Moment,
	isRolloverPreviousDay: boolean,
	dailyRolloverHour: number,
	dailyRolloverMinute: number,
	allowOvertimeRetroactiveRequest = true,
	currentTime?: moment.Moment,
): moment.Moment => {
	// 計算起始時間和結束時間
	let startTime: moment.Moment;
	let endTime: moment.Moment;

	if (isRolloverPreviousDay) {
		// 從前一天 rollover 時間開始
		startTime = targetDate.clone().subtract(1, 'day')
			.hour(dailyRolloverHour)
			.minute(dailyRolloverMinute)
			.second(0);

		// 到當天 rollover 時間前一分鐘結束
		endTime = targetDate.clone()
			.hour(dailyRolloverHour)
			.minute(dailyRolloverMinute)
			.second(0)
			.subtract(1, 'minute');
	} else {
		// 從當天 rollover 時間開始
		startTime = targetDate.clone()
			.hour(dailyRolloverHour)
			.minute(dailyRolloverMinute)
			.second(0);

		// 到隔天 rollover 時間前一分鐘結束
		endTime = targetDate.clone().add(1, 'day')
			.hour(dailyRolloverHour)
			.minute(dailyRolloverMinute)
			.second(0)
			.subtract(1, 'minute');
	}

	// 如果不允許事後加班，起始時間需要考慮當前時間
	// 起始時間 = max(rollover起始時間, 當前時間)
	if (!allowOvertimeRetroactiveRequest && currentTime) {
		startTime = moment.max(startTime, currentTime);
	}

	// 調整時間到有效範圍內
	if (dateTime.isBefore(startTime)) {
		return startTime;
	} else if (dateTime.isAfter(endTime)) {
		return endTime;
	} else {
		return dateTime;
	}
};

// 只縮小手機選項搜尋範圍；日期與事後申請限制仍交由 disabledDate 判斷。
export const getRolloverDateOptionRange = (
	targetDate: moment.Moment,
	isRolloverPreviousDay: boolean,
	dailyRolloverHour: number,
	dailyRolloverMinute: number,
): { start: string; end: string } | undefined => {
	if (!targetDate?.isValid()) return undefined;
	const date = targetDate.format('YYYY-MM-DD');
	if (dailyRolloverHour === 0 && dailyRolloverMinute === 0) return { start: date, end: date };
	return isRolloverPreviousDay ?
		{ start: targetDate.clone().subtract(1, 'day').format('YYYY-MM-DD'), end: date } :
		{ start: date, end: targetDate.clone().add(1, 'day').format('YYYY-MM-DD') };
};

/**
 * 判斷日期選擇器中某個日期是否應該被禁用
 *
 * 禁用規則：
 * 1. 不在 rollover 允許的日期範圍內 → 禁用
 * 2. 如果允許事後加班 → 範圍內的日期都可選
 * 3. 如果不允許事後加班 → 檢查該日期是否還有未來時間可選
 *
 * 特殊情況：
 * - rollover = 00:00: 只允許選擇當天
 *
 * @param current - 正在評估的日期
 * @param targetDate - 認列日期
 * @param isRolloverPreviousDay - 是否從前一天開始
 * @param dailyRolloverHour - 每日 rollover 小時 (0-23)
 * @param dailyRolloverMinute - 每日 rollover 分鐘 (0-59)
 * @param allowOvertimeRetroactiveRequest - 是否允許事後加班申請 (預設: true)
 * @param currentTime - 當前時間，用於事後加班檢查 (預設: 現在)
 * @returns true 表示應該禁用，false 表示可以選擇
 */
export const disabledDate = (
	current: moment.Moment,
	targetDate: moment.Moment,
	isRolloverPreviousDay: boolean,
	dailyRolloverHour: number,
	dailyRolloverMinute: number,
	allowOvertimeRetroactiveRequest = true,
	currentTime?: moment.Moment,
): boolean => {
	if (!current || !targetDate) return true;

	const currentDate = current.format('YYYY-MM-DD');
	const now = currentTime || createZonedMoment()();
	const nowDate = now.format('YYYY-MM-DD');

	// 特殊情況：rollover = 00:00，只允許選擇當天
	if (dailyRolloverHour === 0 && dailyRolloverMinute === 0) {
		const isTargetDate = currentDate === targetDate.format('YYYY-MM-DD');

		if (!isTargetDate) {
			// 不是當天，禁用
			return true;
		}

		// 如果不允許事後加班，且是今天，檢查是否還有未來時間可選
		if (!allowOvertimeRetroactiveRequest && currentDate === nowDate) {
			// 如果當前時間已經是 23:59，則沒有未來時間可選
			if (now.hour() === 23 && now.minute() === 59) {
				return true;
			}
		}

		// 是當天且還有未來時間，可選
		return false;
	}

	// 計算 rollover 允許的日期範圍
	const allowedDates = isRolloverPreviousDay ?
		[
			targetDate.clone().subtract(1, 'day').format('YYYY-MM-DD'),
			targetDate.format('YYYY-MM-DD'),
		] :
		[
			targetDate.format('YYYY-MM-DD'),
			targetDate.clone().add(1, 'day').format('YYYY-MM-DD'),
		];

	// 首先檢查是否在 rollover 允許的日期範圍內
	if (!allowedDates.includes(currentDate)) {
		return true; // 不在範圍內，禁用
	}

	// 如果允許事後加班，到這裡就可以返回 false（日期有效）
	if (allowOvertimeRetroactiveRequest) {
		return false;
	}

	// === 不允許事後加班的情況 ===
	// 需要檢查該日期是否所有有效時間都已過去

	let startDate: string;
	let endDate: string;

	if (isRolloverPreviousDay) {
		startDate = targetDate.clone().subtract(1, 'day').format('YYYY-MM-DD');
		endDate = targetDate.format('YYYY-MM-DD');
	} else {
		startDate = targetDate.format('YYYY-MM-DD');
		endDate = targetDate.clone().add(1, 'day').format('YYYY-MM-DD');
	}

	if (currentDate === startDate) {
		// === 起始日期的檢查 ===

		if (currentDate !== nowDate) {
			// 不是今天，檢查是否為過去的日期
			return current.isBefore(now, 'day');
		}

		// 是今天，檢查當前時間是否已經超過 rollover 起始時間
		if (now.hour() < dailyRolloverHour || (now.hour() === dailyRolloverHour && now.minute() < dailyRolloverMinute)) {
			return true;
		}

		// 當前時間已經超過 rollover 起始時間，檢查是否到了結束時間
		if (now.hour() === 23 && now.minute() === 59) {
			// 已經是今天最後一分鐘，沒有未來時間可選
			return true;
		}

		// 還有未來時間可選
		return false;
	} else if (currentDate === endDate) {
		// === 結束日期的檢查 ===

		if (currentDate !== nowDate) {
			// 不是今天，檢查是否為過去的日期
			return current.isBefore(now, 'day');
		}

		// 是今天，檢查當前時間是否已經超過 rollover 結束時間
		if (dailyRolloverMinute === 0) {
			// rollover 是整點（如 06:00），結束時間是前一分鐘（05:59）
			if (now.hour() >= dailyRolloverHour) {
				// 已超過結束時間
				return true;
			}
			// rollover 有分鐘（如 06:30），結束時間是前一分鐘（06:29）
		} else if (now.hour() > dailyRolloverHour || (now.hour() === dailyRolloverHour && now.minute() >= dailyRolloverMinute)) {
			// 已超過結束時間
			return true;
		}

		// 還沒超過結束時間，還有時間可選
		return false;
	}

	return false;
};

/**
 * 判斷時間選擇器中哪些小時和分鐘應該被禁用
 *
 * 禁用邏輯基於 rollover 配置和事後加班設定：
 *
 * 起始日期：
 * - 禁用 rollover 時間之前的時間
 * - 如果不允許事後加班且是今天，還要禁用當前時間之前
 * - 取兩者中更嚴格的限制
 *
 * 結束日期：
 * - 禁用 rollover 時間之後的時間
 * - 如果不允許事後加班且是今天，還要禁用當前時間之前
 * - 兩種限制同時生效
 *
 * 特殊情況：
 * - rollover = 00:00: 允許整天 (00:00-23:59)，但仍受事後加班限制
 *
 * @param current - 正在評估的日期時間
 * @param targetDate - 認列日期
 * @param isRolloverPreviousDay - 是否從前一天開始
 * @param dailyRolloverHour - 每日 rollover 小時 (0-23)
 * @param dailyRolloverMinute - 每日 rollover 分鐘 (0-59)
 * @param allowOvertimeRetroactiveRequest - 是否允許事後加班申請 (預設: true)
 * @param currentTime - 當前時間，用於事後加班檢查 (預設: 現在)
 * @returns 包含 disabledHours 和 disabledMinutes 函數的物件
 */
export const disabledTime = (
	current: moment.Moment,
	targetDate: moment.Moment,
	isRolloverPreviousDay: boolean,
	dailyRolloverHour: number,
	dailyRolloverMinute: number,
	allowOvertimeRetroactiveRequest = true,
	currentTime?: moment.Moment,
): {
  disabledHours?: () => number[];
  disabledMinutes?: (selectedHour: number
) => number[];
} => {
	if (!targetDate) return {};

	// === 特殊情況：rollover = 00:00 ===
	// 不限制任何時間（允許 00:00-23:59）
	// 但如果不允許事後加班，仍需限制當前時間之前
	if (dailyRolloverHour === 0 && dailyRolloverMinute === 0) {
		if (!allowOvertimeRetroactiveRequest && currentTime) {
			const currentDate = (current || currentTime).format('YYYY-MM-DD');
			const nowDate = currentTime.format('YYYY-MM-DD');

			// 只有在當前日期時才需要限制
			if (currentDate === nowDate) {
				const currentHour = currentTime.hour();
				const currentMinute = currentTime.minute();

				return {
					// 禁用當前小時之前的所有小時
					disabledHours: () => Array.from({ length: currentHour }, (_, i) => i),
					// 在當前小時中，禁用當前分鐘及之前的分鐘
					disabledMinutes: (selectedHour: number) => {
						if (selectedHour === currentHour) {
							return Array.from({ length: currentMinute + 1 }, (_, i) => i);
						}
						return [];
					},
				};
			}
		}
		// 允許事後加班或不是今天，不限制時間
		return {};
	}

	// === 一般情況：有 rollover 時間 ===
	const currentDate = (current || currentTime || createZonedMoment()()).format('YYYY-MM-DD');

	// 計算起始日期和結束日期
	let startDate: string;
	let endDate: string;

	if (isRolloverPreviousDay) {
		startDate = targetDate.clone().subtract(1, 'day').format('YYYY-MM-DD');
		endDate = targetDate.format('YYYY-MM-DD');
	} else {
		startDate = targetDate.format('YYYY-MM-DD');
		endDate = targetDate.clone().add(1, 'day').format('YYYY-MM-DD');
	}

	if (currentDate === startDate) {
		// === 起始日期：禁用 rollover 時間之前 ===
		let minHour = dailyRolloverHour;
		let minMinute = dailyRolloverMinute;

		// 如果不允許事後加班，且當前日期等於起始日期（是今天）
		if (!allowOvertimeRetroactiveRequest && currentTime) {
			const nowDate = currentTime.format('YYYY-MM-DD');
			if (currentDate === nowDate) {
				const currentHour = currentTime.hour();
				const currentMinute = currentTime.minute();

				// 取最嚴格的限制（較晚的時間）
				// 例如：rollover = 06:00，當前時間 = 10:30
				//    → 使用 10:30（更嚴格）
				if (currentHour > minHour || (currentHour === minHour && currentMinute > minMinute)) {
					minHour = currentHour;
					minMinute = currentMinute;
				}
			}
		}

		return {
			// 禁用 minHour 之前的所有小時
			// 例如：minHour = 10 → 禁用 [0, 1, 2, ..., 9]
			disabledHours: () => Array.from({ length: minHour }, (_, i) => i),
			// 在 minHour 這個小時中，禁用 minMinute 之前的分鐘
			// 例如：minHour = 10, minMinute = 30
			//    → 在 10 時中禁用 [0, 1, 2, ..., 29]
			disabledMinutes: (selectedHour: number) => {
				if (selectedHour === minHour) {
					return Array.from({ length: minMinute }, (_, i) => i);
				}
				return [];
			},
		};
	} else if (currentDate === endDate) {
		// === 結束日期：禁用 rollover 時間之後 + 當前時間之前 ===

		const rolloverHour = dailyRolloverHour;
		const rolloverMinute = dailyRolloverMinute;

		let currentHourLimit = 0;
		let currentMinuteLimit = 0;

		// 如果不允許事後加班，且當前日期等於結束日期（是今天）
		if (!allowOvertimeRetroactiveRequest && currentTime) {
			const nowDate = currentTime.format('YYYY-MM-DD');
			if (currentDate === nowDate) {
				currentHourLimit = currentTime.hour();
				currentMinuteLimit = currentTime.minute();
			}
		}

		return {
			disabledHours: () => {
				const disabledHours: number[] = [];

				// 1. 禁用當前時間之前的小時
				// 例如：currentHourLimit = 2 → 禁用 [0, 1]
				for (let i = 0; i < currentHourLimit; i += 1) {
					disabledHours.push(i);
				}

				// 2. 禁用 rollover 時間之後的小時
				if (rolloverMinute === 0) {
					// rollover 是整點（如 04:00）
					// 結束時間是前一分鐘（03:59）
					// 從 rollover 小時開始禁用
					// 例如：rollover = 04:00 → 禁用 [4, 5, 6, ..., 23]
					for (let i = rolloverHour; i < 24; i += 1) {
						if (!disabledHours.includes(i)) {
							disabledHours.push(i);
						}
					}
				} else {
					// rollover 有分鐘（如 04:30）
					// 結束時間是前一分鐘（04:29）
					// 從下一個小時開始禁用
					// 例如：rollover = 04:30 → 禁用 [5, 6, 7, ..., 23]
					//  ->（04:00-04:29 還可以選，04:30+ 不行）
					for (let i = rolloverHour + 1; i < 24; i += 1) {
						if (!disabledHours.includes(i)) {
							disabledHours.push(i);
						}
					}
				}

				return disabledHours.sort((a, b) => a - b);
			},
			disabledMinutes: (selectedHour: number) => {
				const disabledMinutes: number[] = [];

				// 1. 如果是當前小時，禁用當前分鐘之前
				// 例如：selectedHour = 2, currentHourLimit = 2, currentMinuteLimit = 30
				//   → 禁用 [0, 1, 2, ..., 29]
				if (selectedHour === currentHourLimit) {
					for (let i = 0; i < currentMinuteLimit; i += 1) {
						disabledMinutes.push(i);
					}
				}

				// 2. 如果是 rollover 小時且有分鐘限制，禁用該分鐘之後
				// 例如：selectedHour = 4, rolloverHour = 4, rolloverMinute = 30
				//   → 禁用 [30, 31, 32, ..., 59]
				if (selectedHour === rolloverHour && rolloverMinute > 0) {
					for (let i = rolloverMinute; i < 60; i += 1) {
						if (!disabledMinutes.includes(i)) {
							disabledMinutes.push(i);
						}
					}
				}

				return disabledMinutes.sort((a, b) => a - b);
			},
		};
	}

	return {};
};

export const formatShiftItem = (shift: any, punchInShift: any, timezone?: string): string => {
	const userMoment = createZonedMoment(timezone);
	const { allow_flextime, allow_flextime_before, allow_flextime_after } =
		punchInShift;
	const startTime = userMoment(shift.shift_start_datetime)
		.subtract(allow_flextime ? allow_flextime_before || 0 : 0, 'm')
		.format('MM/DD HH:mm');
	const endTime = userMoment(shift.shift_end_datetime)
		.add(allow_flextime ? allow_flextime_after || 0 : 0, 'm')
		.format('MM/DD HH:mm');
	return `${shift.shift_name} (${startTime} ~ ${endTime})`;
};
